import * as NavigationMenu from '@radix-ui/react-navigation-menu';

import { Typography } from '@/components';
import { PromoCtaPill } from '@/theme/header/components/PromoCtaPill/PromoCtaPill';
import { getRel, getTarget } from '@/theme/shared/utils';

import styles from './MegaMenu.module.scss';
import { hasChildren } from '../../../utils';

import type { HeaderMenuItem } from '../../../types';

type MegaMenuProps = {
  item: HeaderMenuItem | null;
};

type ColumnGroup = {
  key: string;
  sections: HeaderMenuItem[];
};

function groupSectionsByColumn(sections: HeaderMenuItem[]): ColumnGroup[] {
  const groups: ColumnGroup[] = [];
  const groupMap = new Map<string, ColumnGroup>();

  for (const section of sections) {
    const key = section.attrTitle || String(section.id);

    if (groupMap.has(key)) {
      groupMap.get(key)!.sections.push(section);
    } else {
      const group: ColumnGroup = { key, sections: [section] };
      groups.push(group);
      groupMap.set(key, group);
    }
  }

  return groups;
}

export function MegaMenu({ item }: MegaMenuProps) {
  if (!item || !hasChildren(item)) {
    return null;
  }

  const sections = item.children ?? [];
  const hasPromo = Boolean(item.promo);
  const hasGrouping = sections.some((s) => s.attrTitle);

  if (hasGrouping) {
    const columnGroups = groupSectionsByColumn(sections);

    return (
      <div className={styles.megaInner}>
        <div className={styles.megaGrouped} data-column-count={columnGroups.length}>
          {columnGroups.map((group, index) => {
            const isFeatured = index === 0;

            if (isFeatured) {
              return (
                <div key={group.key} className={styles.megaFeaturedColumn}>
                  <div className={styles.megaFeaturedHighlight}>
                    {group.sections.map((section) => (
                      <MegaMenuColumn key={section.id} section={section} />
                    ))}
                  </div>
                  {hasPromo && <MegaMenuPromo promo={item.promo} />}
                </div>
              );
            }

            return (
              <div key={group.key} className={styles.megaColumnGroup}>
                <div className={styles.megaColumnGroupInner}>
                  {group.sections.map((section) => (
                    <MegaMenuColumn key={section.id} section={section} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Existing flat grid layout (backward compatible)
  const isCompactPromo = hasPromo && item.promo?.compact;
  const columnCount = Math.min(4, sections.length + (hasPromo ? (isCompactPromo ? 2 : 1) : 0));

  return (
    <div className={styles.megaInner}>
      <div className={styles.megaGrid} data-column-count={columnCount}>
        {sections.map((section) => (
          <MegaMenuColumn key={section.id} section={section} />
        ))}

        {hasPromo && <MegaMenuPromo promo={item.promo} />}
      </div>
    </div>
  );
}

type MegaMenuColumnProps = {
  section: HeaderMenuItem;
};

const P_AND_D_GUIDE_PATH = '/guides/planning-deploying-successful-intranet/';

function trackPromoClick(url: string) {
  if (url.includes(P_AND_D_GUIDE_PATH)) {
    window.InteractTracking?.track('p_and_d_guide_nav_download', {
      page_name: window.InteractTracking?.getContext().page_name || '',
    });
  }
}

function trackSubMenu(menuItem: string, parentItem: string) {
  window.InteractTracking?.track('menu_interaction', {
    menu_item_name: menuItem,
    menu_level: 'sub',
    parent_item: parentItem,
  });
}

function MegaMenuColumn({ section }: MegaMenuColumnProps) {
  const childLinks = section.children ?? [];

  if (!childLinks.length) {
    return (
      <div className={styles.megaColumn}>
        <NavigationMenu.Link asChild>
          <a
            className={styles.megaLinkStandalone}
            href={section.url || '#'}
            target={getTarget(section.target)}
            rel={getRel(section.target)}
            onClick={() => trackSubMenu(section.title, section.title)}
          >
            <Typography tagName="span" singleLine size="medium" color="inherit">
              {section.title}
            </Typography>
          </a>
        </NavigationMenu.Link>
      </div>
    );
  }

  return (
    <div className={styles.megaColumn}>
      {!section.hideLabel && section.title ? (
        <Typography
          variant="label"
          size="small"
          color="var(--color-text-brand-primary)"
          className={styles.megaLabel}
          data-label={section.iconKey || section.title || undefined}
        >
          {section.title}
        </Typography>
      ) : null}
      <ul className={styles.megaList}>
        {childLinks.map((link) => (
          <li key={link.id}>
            <NavigationMenu.Link asChild>
              <a
                className={styles.megaLink}
                href={link.url || '#'}
                target={getTarget(link.target)}
                rel={getRel(link.target)}
                data-overview={link.overview ? true : undefined}
                aria-current={link.current ? 'page' : undefined}
                onClick={() => trackSubMenu(link.title, section.title)}
              >
                <Typography
                  tagName="span"
                  singleLine={!link.overview}
                  size="medium"
                  color="inherit"
                >
                  {link.title}
                </Typography>
              </a>
            </NavigationMenu.Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

type MegaMenuPromoProps = {
  promo?: HeaderMenuItem['promo'];
};

function MegaMenuPromo({ promo }: MegaMenuPromoProps) {
  if (
    !promo ||
    (!promo.title &&
      !promo.heading &&
      !promo.description &&
      !promo.ctaLabel &&
      !promo.ctaOverlay &&
      !promo.image)
  ) {
    return null;
  }

  const promoImage = promo.image;
  const promoAlt = promoImage?.alt ?? promo.title ?? 'Promotional highlight';

  const card = (
    <div
      className={styles.megaPromoCard}
      data-tint={promo.pill || undefined}
      data-compact={promo.compact || undefined}
      data-feature={promo.feature || undefined}
      data-heading={promo.heading ? '' : undefined}
      data-cta={promo.ctaOverlay ? '' : undefined}
      style={promo.maxHeight ? { maxHeight: promo.maxHeight } : undefined}
    >
      {promoImage ? (
        <img
          className={styles.megaPromoMedia}
          src={promoImage.url}
          alt={promoAlt}
          width={promoImage.width}
          height={promoImage.height}
          loading="lazy"
        />
      ) : (
        <div className={styles.megaPromoPlaceholder} aria-hidden="true"></div>
      )}

      {promo.heading && (
        <div
          className={styles.megaPromoHeading}
          dangerouslySetInnerHTML={{ __html: promo.heading }}
        />
      )}

      {promo.ctaOverlay && <PromoCtaPill label={promo.ctaOverlay} />}

      {promo.pill && promo.ctaLabel && (
        <Typography
          tagName="span"
          variant="eyebrow"
          size="small"
          color="inherit"
          className={styles.megaPromoPill}
        >
          {promo.ctaLabel}
        </Typography>
      )}

      {(promo.title || promo.description || (!promo.pill && promo.ctaLabel)) && (
        <div className={styles.megaPromoBody}>
          {!promo.pill && promo.ctaLabel && (
            <Typography
              tagName="span"
              variant="eyebrow"
              size="small"
              color="var(--color-text-default-soft-light)"
              className={styles.megaPromoEyebrow}
            >
              {promo.ctaLabel}
            </Typography>
          )}
          {promo.title && (
            <Typography variant="eyebrow" color="var(--color-text-default-default-light)">
              {promo.title}
            </Typography>
          )}
          {promo.description && (
            <Typography variant="eyebrow" size="small" color="var(--color-text-default-soft-light)">
              {promo.description}
            </Typography>
          )}
        </div>
      )}
    </div>
  );

  if (promo.ctaUrl) {
    return (
      <NavigationMenu.Link asChild>
        <a
          className={styles.megaPromoLink}
          href={promo.ctaUrl}
          target={getTarget(promo.ctaTarget)}
          rel={getRel(promo.ctaTarget)}
          data-compact={promo.compact || undefined}
          data-feature={promo.feature || undefined}
          onClick={() => trackPromoClick(promo.ctaUrl!)}
        >
          {card}
        </a>
      </NavigationMenu.Link>
    );
  }

  return card;
}
